import React, { useState } from "react";
import { FaChevronDown, FaChevronUp, FaExclamationTriangle, FaTimes } from "react-icons/fa";
import { MdChevronRight, MdChevronLeft } from "react-icons/md";
import { useGetHallOfShameQuery } from "../../features/api/appApi";

const HallOfShame = () => {
  const { data: offenders = [] } = useGetHallOfShameQuery();
  const [expandedId, setExpandedId] = useState(null);
  const [selectedProof, setSelectedProof] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const itemsPerPage = 10;
  const totalPages = Math.ceil(offenders.length / itemsPerPage);
  const paginatedOffenders = offenders.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const toggleExpand = (id) => setExpandedId((prev) => (prev === id ? null : id));

  return (
    <main className="home-shell min-h-screen px-4 pb-14 pt-28 sm:px-6">
      <section className="home-section border-none px-0 py-0">
        <div className="home-container">
          <div className="home-card-strong p-8 sm:p-10">
            <span className="section-kicker">Policy Transparency</span>
            <h1 className="home-title mt-4 text-3xl font-bold sm:text-5xl">
              Hall of <span className="home-accent-gradient">Shame</span>
            </h1>
            <p className="mt-4 max-w-3xl text-sm sm:text-base home-muted">
              Records of confirmed academic integrity violations in the programming community.
            </p>
          </div>

          <div className="home-card mt-4 p-4 text-sm home-muted">
            <FaExclamationTriangle className="mr-2 inline home-accent" />
            Cases are published only after verification with evidence and review.
          </div>
        </div>
      </section>

      <section className="home-section border-none px-0 pt-8">
        <div className="home-container">
          {paginatedOffenders.length === 0 ? (
            <div className="home-card p-12 text-center home-muted">No records available right now.</div>
          ) : (
            <div className="home-card overflow-hidden">
              {paginatedOffenders.map((offender) => (
                <div key={offender.id} className="border-b p-5" style={{ borderColor: "var(--border)" }}>
                  <div className="flex items-start gap-4">
                    <img className="h-14 w-14 rounded-full object-cover" src={offender.imageUrl} alt={offender.name} />
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div>
                          <h3 className="font-semibold">{offender.name}</h3>
                          <p className="text-xs home-muted">ID: {offender.studentId || "N/A"}</p>
                        </div>
                        <span className="home-pill px-2 py-1 text-xs">{new Date(offender.date).toLocaleDateString()}</span>
                      </div>

                      <p className="mt-2 text-sm">
                        Violation: <span className="home-accent font-semibold">{offender.issue}</span>
                      </p>

                      {expandedId === offender.id ? (
                        <div className="home-card-strong mt-3 p-4">
                          <p className="text-sm home-muted">{offender.details}</p>
                          {offender.proofUrl ? (
                            <button onClick={() => setSelectedProof(offender.proofUrl)} className="home-btn-primary mt-3 px-3 py-2 text-xs font-semibold">
                              View Proof
                            </button>
                          ) : null}
                        </div>
                      ) : null}

                      <button onClick={() => toggleExpand(offender.id)} className="mt-3 inline-flex items-center gap-1 text-sm font-medium home-accent">
                        {expandedId === offender.id ? <FaChevronUp /> : <FaChevronDown />} {expandedId === offender.id ? "Hide" : "Show"} Details
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {totalPages > 1 ? (
            <div className="home-card mt-4 flex items-center justify-between p-3 text-sm">
              <button onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} className="home-btn-secondary px-3 py-2" disabled={currentPage === 1}>
                <MdChevronLeft className="inline" /> Previous
              </button>
              <span className="home-muted">Page {currentPage} of {totalPages}</span>
              <button onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} className="home-btn-secondary px-3 py-2" disabled={currentPage === totalPages}>
                Next <MdChevronRight className="inline" />
              </button>
            </div>
          ) : null}
        </div>
      </section>

      {selectedProof ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="home-card-strong max-h-[90vh] w-full max-w-4xl overflow-auto">
            <div className="flex items-center justify-between border-b p-4" style={{ borderColor: "var(--border)" }}>
              <h3 className="text-lg font-semibold">Evidence of Violation</h3>
              <button onClick={() => setSelectedProof(null)} className="home-btn-secondary px-2 py-1">
                <FaTimes />
              </button>
            </div>
            <div className="p-4">
              {selectedProof.endsWith(".pdf") ? (
                <iframe src={selectedProof} className="h-[70vh] w-full" frameBorder="0" title="Proof document" />
              ) : (
                <img src={selectedProof} alt="Proof of violation" className="mx-auto max-h-[70vh] w-full object-contain" />
              )}
            </div>
          </div>
        </div>
      ) : null}
    </main>
  );
};

export default HallOfShame;
